import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryKey
} from "@tanstack/react-query";
import { fetcherFactory } from "../services";
import { useAuthStore } from "../stores";
import { getEnvironmentVariables } from "../utils/general";
import { normalizeMfaUserSettings, type MfaPreference, type MfaUserSettings } from "../utils/mfa";

const { basePath } = getEnvironmentVariables();
const fetcher = fetcherFactory();

const mfaUserSettingsUri = "/usermgmt/user/mfa-user-settings";

export type { MfaFactor, MfaUserSettings } from "../utils/mfa";

/**
 * Stato desiderato completo: il BE imposta sempre entrambi i fattori, quelli omessi non restano invariati.
 * Campi in snake_case come confermato dal BE (la specifica indicava camelCase).
 */
export type MfaUserSettingsPayload = {
  email_enabled: boolean
  totp_enabled: boolean
  preferred_mfa: MfaPreference
}

const getMfaUserSettingsQueryKey = (sessionToken: string): QueryKey => [
  sessionToken,
  mfaUserSettingsUri
];

export function useMfaUserSettingsQuery({
  enabled = true,
  queryKey = [],
  refetchOnMount = true
}: {
  enabled?: boolean
  queryKey?: QueryKey
  refetchOnMount?: boolean | "always"
} = {}) {
  const accessToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const isSeedingSession = useAuthStore(state => state.hasSeedSession);

  return useQuery<
    MfaUserSettings,
    unknown,
    MfaUserSettings,
    QueryKey
  >({
    enabled: Boolean(enabled && accessToken && sessionToken) && !isSeedingSession,
    queryFn: () => fetcher(
      `${basePath}${mfaUserSettingsUri}`,
      {
        headers: {
          "Accept": "application/json",
          "Authorization": accessToken,
          "x-ada-session-token": sessionToken
        },
        method: "GET"
      })
      .then((res) => res.json())
      .then(normalizeMfaUserSettings),
    queryKey: [...getMfaUserSettingsQueryKey(sessionToken), accessToken, ...queryKey],
    refetchOnMount
  });
}

export function useMfaUserSettingsMutation() {
  const queryClient = useQueryClient();
  const accessToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";

  return useMutation({
    mutationFn: (payload: MfaUserSettingsPayload) => fetcher(
      `${basePath}${mfaUserSettingsUri}`,
      {
        body: JSON.stringify(payload),
        headers: {
          "Accept": "application/json",
          "Authorization": accessToken,
          "Content-Type": "application/json",
          "x-ada-session-token": sessionToken
        },
        method: "POST"
      })
      // Il fetcher verifica l'esito HTTP; il body del salvataggio non viene usato e può essere vuoto.
      .then(() => undefined),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: getMfaUserSettingsQueryKey(sessionToken)
      });
    }
  });
}
